import React, { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom-v5-compat";

import { useGetTransitionsDto } from "@fartherfinance/frontend/api/TransitionWorkflows/hooks/useGetTransitionsDto";
import { GetTransitionsQueryParams } from "@fartherfinance/frontend/api/TransitionWorkflows/requests/getTransitions";
import { PaginationMetadata } from "@fartherfinance/frontend/api/Types";

import Button from "@src/multiCustodian/components/MUI/Button/Button";
import { usePersistedQueryParams } from "@src/multiCustodian/hooks/Advisor/usePersistedQueryParams";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import CreateTransitionModal from "@src/multiCustodian/pages/Advisor/TransitionWorkflows/CreateTransitionModal/CreateTransitionModal";

import AdvisorContainer from "./AdvisorContainer";
import AdvisorContentContainer from "./AdvisorContentContainer";
import { TRANSITIONS_DEFAULT_QUERY_PARAMS } from "./TransitionWorkflows/common";
import PipelineSummary from "./TransitionWorkflows/PipelineSummary/PipelineSummary";
import TransitionsTable from "./TransitionWorkflows/TransitionsTable/TransitionsTable";

const TransitionWorkflows: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const navigate = useNavigate();

  const [showCreateTransitionModal, setShowCreateTransitionModal] =
    useState(false);

  // TODO: Uncomment this when the tracking event is needed
  // useEffect(() => {
  //   trackEvent({ name: "Advisor Open Transition List" });
  // }, []);

  const [paginationState, setPaginationState] = useState<PaginationMetadata>();

  const [queryParams, setQueryParams] =
    usePersistedQueryParams<GetTransitionsQueryParams>(
      TRANSITIONS_DEFAULT_QUERY_PARAMS,
      {
        parseBooleans: true,
        decode: false,
        arrayFormat: "bracket-separator",
      }
    );

  const auth = useAdvisorRequestAuth();
  const transitionsQuery = useGetTransitionsDto(auth, queryParams);

  const { advisorTransitions = [], pagination } = transitionsQuery.data ?? {};

  const handleChangeQueryParams = (
    newQueryParams: GetTransitionsQueryParams
  ): void => {
    setQueryParams({ ...queryParams, ...newQueryParams });
  };

  useEffect(() => {
    // NOTE: we have to persist pagination values locally as they become undefined inbetween request
    if (pagination) {
      setPaginationState(pagination);
    }
  }, [pagination]);

  return (
    <AdvisorContainer>
      <AdvisorContentContainer
        hasPadding={false}
        heroTitle="Transitions"
        heroEnd={
          <Button
            sx={{ width: 160 }}
            variant="contained"
            size="medium"
            buttonType="primary"
            text="Create Transition"
            onClick={() => setShowCreateTransitionModal(true)}
          />
        }
      >
        {showCreateTransitionModal && (
          <CreateTransitionModal
            isOpen={showCreateTransitionModal}
            onClose={() => setShowCreateTransitionModal(false)}
            onSubmit={() => setShowCreateTransitionModal(false)}
          />
        )}

        <PipelineSummary advisorCount={pagination?.totalCount ?? 0} />

        <TransitionsTable
          transitions={advisorTransitions}
          pagination={paginationState}
          isLoading={transitionsQuery.isLoading}
          isErrored={transitionsQuery.hasError}
          handleChangeQueryParams={handleChangeQueryParams}
          onTransitionClick={(transitionId) =>
            navigate(`/Advisor/Transitions/${transitionId}`)
          }
        />

        {children}
      </AdvisorContentContainer>
    </AdvisorContainer>
  );
};

export default TransitionWorkflows;
